// src/services/porterService.js
import axios from 'axios';

const PORTER_API_BASE_URL = 'https://pfe-apigwuat.porter.in'; // UAT testing environment
const PORTER_API_KEY = 'YOUR_PORTER_API_KEY'; // Replace with your actual API key

// Create axios instance with default configuration
const porterApi = axios.create({
  baseURL: PORTER_API_BASE_URL,
  headers: {
    'X-API-KEY': PORTER_API_KEY,
    'Content-Type': 'application/json'
  }
});

/**
 * Get a quote from Porter API based on pickup and drop locations
 * @param {Object} pickupLocation - Object containing lat/lng coordinates for pickup
 * @param {Object} dropLocation - Object containing lat/lng coordinates for drop
 * @param {Object} customer - Object containing customer details (name, mobile)
 * @returns {Promise} - Promise containing quote data
 */
export const getPorterQuote = async (pickupLocation, dropLocation, customer) => {
  try {
    const response = await porterApi.get('/v1/get_quote', {
      data: {
        pickup_details: {
          lat: pickupLocation.lat,
          lng: pickupLocation.lng
        },
        drop_details: {
          lat: dropLocation.lat,
          lng: dropLocation.lng
        },
        customer: {
          name: customer.name,
          mobile: {
            country_code: "+91", // Using India's country code by default
            number: customer.phoneNumber.replace(/^\+91/, '') // Remove country code if present
          }
        }
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching Porter quote:', error);
    
    // Format error message based on API error response
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get quote from Porter');
    }
    
    throw new Error('Failed to get quote from Porter');
  }
};

/**
 * Create a new delivery order with Porter
 * @param {String} requestId - Unique identifier for this request
 * @param {Object} pickupDetails - Pickup address and contact information
 * @param {Object} dropDetails - Drop address and contact information
 * @param {String} additionalComments - Additional instructions for the delivery (optional)
 * @param {Array} deliveryInstructions - Special instructions for driver (optional)
 * @returns {Promise} - Promise containing the created order data
 */
export const createPorterOrder = async (requestId, pickupDetails, dropDetails, additionalComments = '', deliveryInstructions = []) => {
  try {
    const payload = {
      request_id: requestId,
      pickup_details: {
        address: formatAddressForPorter(pickupDetails)
      },
      drop_details: {
        address: formatAddressForPorter(dropDetails)
      }
    };
    
    // Add optional fields if they exist
    if (additionalComments) {
      payload.additional_comments = additionalComments;
    }
    
    if (deliveryInstructions && deliveryInstructions.length > 0) {
      payload.delivery_instructions = {
        instructions_list: deliveryInstructions.map(instruction => ({
          type: 'text',
          description: instruction
        }))
      };
    }
    
    const response = await porterApi.post('/v1/orders/create', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating Porter order:', error);
    
    // Format error message based on API error response
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to create order with Porter');
    }
    
    throw new Error('Failed to create order with Porter');
  }
};

/**
 * Track an existing Porter order
 * @param {String} orderId - Porter order ID to track
 * @returns {Promise} - Promise containing the order tracking data
 */
export const trackPorterOrder = async (orderId) => {
  try {
    const response = await porterApi.get(`/v1.1/orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error tracking Porter order:', error);
    
    // Format error message based on API error response
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to track Porter order');
    }
    
    throw new Error('Failed to track Porter order');
  }
};

/**
 * Format address in the structure required by Porter API
 * @param {Object} address - Address object to format
 * @returns {Object} - Formatted address object
 */
const formatAddressForPorter = (address) => {
  const contactDetails = {
    name: address.contactName,
    phone_number: address.contactPhone.startsWith('+91') 
      ? address.contactPhone 
      : `+91${address.contactPhone}`
  };

  return {
    apartment_address: address.apartmentAddress || '',
    street_address1: address.street || '',
    street_address2: address.streetAddress2 || '',
    landmark: address.landmark || '',
    city: address.city || 'Bengaluru', // Default to Bengaluru if not provided
    state: address.state || 'Karnataka', // Default to Karnataka if not provided
    pincode: address.pincode || '',
    country: address.country || 'India',
    lat: address.lat,
    lng: address.lng,
    contact_details: contactDetails
  };
};

/**
 * Generate a UUID to use as request_id for Porter API
 * @returns {String} - UUID string
 */
export const generateRequestId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export default {
  getPorterQuote,
  createPorterOrder,
  trackPorterOrder,
  generateRequestId
};